import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';

/**
 * Bottom sheet on a phone, centred panel on a laptop.
 *
 * Deliberately not dismissible by tapping the backdrop: these overlays hold a half-finished
 * payment or a price override, and losing one to a stray thumb at a busy counter is worse than
 * one extra tap on Cancel.
 *
 * Rendered into <body>, not where it is declared. `fixed` is fixed to the screen only while no
 * ancestor is animating a transform, and the entrance animations fill `both`, so every card
 * they touch keeps one after it has finished arriving. Declared inside such a card (the sync
 * status on the home screen), the backdrop covered that card alone and the cards after it
 * painted straight over the dialog.
 */
export function Modal({
  titulo,
  onCerrar,
  children,
  pie,
  guia,
  cedeAlLector = false,
  ancho = 'normal',
}: {
  titulo: string;
  onCerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
  /** A lesson's anchor (`data-guia`) on the whole panel, its buttons included. */
  guia?: string;
  /** Asks nothing, and the screen closes it at the next scan: a USB scanner's code goes through to the screen (`lib/lector-usb.ts`). */
  cedeAlLector?: boolean;
  /**
   * «amplio»: wider on a laptop, for a sheet that sets figures side by side (an offer's before and
   * after, `HojaOferta`). A phone's is the screen's width either way.
   */
  ancho?: 'normal' | 'amplio';
}) {
  const t = useT();
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alTeclado);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', alTeclado);
      document.body.style.overflow = '';
    };
  }, [onCerrar]);

  return createPortal(
    <div className="aparece fixed inset-0 z-40 flex items-end justify-center bg-black/70 sm:items-center">
      <div
        className={`surge flex max-h-[92dvh] w-full flex-col rounded-t-3xl bg-slate-900 shadow-2xl shadow-black/60 ring-1 ring-white/10 ring-inset sm:rounded-3xl ${ancho === 'amplio' ? 'max-w-2xl' : 'max-w-lg'}`}
        data-guia={guia}
        data-cede-al-lector={cedeAlLector || undefined}
      >
        <header className="flex items-center justify-between gap-3 border-b border-white/5 px-5 py-4">
          <h2 className="min-w-0 truncate text-lg font-semibold">{titulo}</h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label={t('comun.cerrar')}
            className="-mr-2 flex size-11 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
          >
            <Ico icono="cerrar" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {pie && <div className="border-t border-white/5 px-5 py-4">{pie}</div>}
      </div>
    </div>,
    document.body,
  );
}
